import {
  ConsolidateLanguagesRequest,
  ConsolidateProficienciesRequest,
  Language,
  LanguageCreateRequest,
  LanguageUpdateRequest,
  Proficiency,
  ProficiencyCreateRequest,
  ProficiencyUpdateRequest,
} from "../../models/language.model";
import { BackendHttpService } from "../backendHttpService";

/**
 * Language Dictionary Admin Service
 *
 * Administrative endpoints for language dictionary management including:
 * - Language dictionary CRUD operations
 * - Proficiency dictionary CRUD operations
 * - Consolidation operations for both dictionaries
 *
 * These endpoints are typically restricted to administrators.
 */
export class LanguageDictionaryAdminService extends BackendHttpService {
  // Cache for each dictionary
  private languagesDictionaryCache: Language[] | null = null;
  private proficienciesDictionaryCache: Proficiency[] | null = null;

  constructor() {
    super("languagedictionaries");
  }

  // Clear specific cache when the corresponding dictionary changes
  private clearLanguagesCache(): void {
    this.languagesDictionaryCache = null;
  }

  private clearProficienciesCache(): void {
    this.proficienciesDictionaryCache = null;
  }

  // ===== LANGUAGE DICTIONARY METHODS (ADMIN ONLY) =====

  /**
   * Get all languages from dictionary
   * GET /languagedictionaries/languages
   */
  async getLanguages(): Promise<Language[]> {
    if (this.languagesDictionaryCache) {
      return this.languagesDictionaryCache;
    }
    try {
      this.languagesDictionaryCache = await this.get<Language[]>("/languages");
      return this.languagesDictionaryCache;
    } catch (error) {
      console.error("Error getting languages:", error);
      throw error;
    }
  }

  /**
   * Create a new language in dictionary
   * POST /languagedictionaries/languages
   */
  async createLanguage(request: LanguageCreateRequest): Promise<Language> {
    try {
      const language = await this.post<LanguageCreateRequest, Language>(
        request,
        "/languages"
      );
      this.clearLanguagesCache();
      return language;
    } catch (error) {
      console.error("Error creating language:", error);
      throw error;
    }
  }

  /**
   * Update an existing language in dictionary
   * PUT /languagedictionaries/languages/{languageId}
   */
  async updateLanguage(
    languageId: number,
    request: LanguageUpdateRequest
  ): Promise<Language> {
    try {
      const language = await this.put<LanguageUpdateRequest, Language>(
        request,
        `/languages/${languageId}`
      );
      this.clearLanguagesCache();
      return language;
    } catch (error) {
      console.error("Error updating language:", error);
      throw error;
    }
  }

  /**
   * Delete a language from dictionary
   * DELETE /languagedictionaries/languages/{languageId}
   */
  async deleteLanguage(languageId: number): Promise<void> {
    try {
      await this.delete(`/languages/${languageId}`);
      this.clearLanguagesCache();
    } catch (error) {
      console.error("Error deleting language:", error);
      throw error;
    }
  }

  /**
   * Consolidate languages in dictionary
   * POST /languagedictionaries/languages/consolidate
   */
  async consolidateLanguages(
    request: ConsolidateLanguagesRequest
  ): Promise<Language> {
    try {
      const language = await this.post<ConsolidateLanguagesRequest, Language>(
        request,
        "/languages/consolidate"
      );
      this.clearLanguagesCache();
      return language;
    } catch (error) {
      console.error("Error consolidating languages:", error);
      throw error;
    }
  }

  // ===== PROFICIENCY DICTIONARY METHODS (ADMIN ONLY) =====

  /**
   * Get all proficiencies from dictionary
   * GET /languagedictionaries/proficiencies
   */
  async getProficiencies(): Promise<Proficiency[]> {
    if (this.proficienciesDictionaryCache) {
      return this.proficienciesDictionaryCache;
    }
    try {
      this.proficienciesDictionaryCache = await this.get<Proficiency[]>(
        "/proficiencies"
      );
      return this.proficienciesDictionaryCache;
    } catch (error) {
      console.error("Error getting proficiencies:", error);
      throw error;
    }
  }

  /**
   * Create a new proficiency in dictionary
   * POST /languagedictionaries/proficiencies
   */
  async createProficiency(
    request: ProficiencyCreateRequest
  ): Promise<Proficiency> {
    try {
      const proficiency = await this.post<
        ProficiencyCreateRequest,
        Proficiency
      >(request, "/proficiencies");
      this.clearProficienciesCache();
      return proficiency;
    } catch (error) {
      console.error("Error creating proficiency:", error);
      throw error;
    }
  }

  /**
   * Update an existing proficiency in dictionary
   * PUT /languagedictionaries/proficiencies/{proficiencyId}
   */
  async updateProficiency(
    proficiencyId: number,
    request: ProficiencyUpdateRequest
  ): Promise<Proficiency> {
    try {
      const proficiency = await this.put<ProficiencyUpdateRequest, Proficiency>(
        request,
        `/proficiencies/${proficiencyId}`
      );
      this.clearProficienciesCache();
      return proficiency;
    } catch (error) {
      console.error("Error updating proficiency:", error);
      throw error;
    }
  }

  /**
   * Delete a proficiency from dictionary
   * DELETE /languagedictionaries/proficiencies/{proficiencyId}
   */
  async deleteProficiency(proficiencyId: number): Promise<void> {
    try {
      await this.delete(`/proficiencies/${proficiencyId}`);
      this.clearProficienciesCache();
    } catch (error) {
      console.error("Error deleting proficiency:", error);
      throw error;
    }
  }

  /**
   * Consolidate proficiencies in dictionary
   * POST /languagedictionaries/proficiencies/consolidate
   */
  async consolidateProficiencies(
    request: ConsolidateProficienciesRequest
  ): Promise<Proficiency> {
    try {
      const proficiency = await this.post<
        ConsolidateProficienciesRequest,
        Proficiency
      >(request, "/proficiencies/consolidate");
      this.clearProficienciesCache();
      return proficiency;
    } catch (error) {
      console.error("Error consolidating proficiencies:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const languageDictionaryAdminService =
  new LanguageDictionaryAdminService();
